"use client";

import { useEffect } from "react";
import { Anchor, Badge, Group, Stack, Text } from "@mantine/core";
import { useStripeTerminalContext } from "@/app/_providers/StripeTerminalProvider";
import { StripeTerminalReader } from "@/app/@types/stripe";

// Stripe's status in plain words. "Ready" means the reader is on and talking
// to Stripe. Nothing is ever "connected" between payments any more, so that
// word never shows; a device only holds the reader while a payment runs.
// Stripe's status lags, so this is display only and never blocks a payment.
export const ReaderStatusBadge = ({
  status,
}: {
  status: StripeTerminalReader["status"] | undefined;
}) =>
  status === "online" ? (
    <Badge color="green" variant="light" size="sm">
      Ready
    </Badge>
  ) : (
    <Badge color="gray" variant="light" size="sm">
      Offline
    </Badge>
  );

// Ask Stripe for fresh reader status when a payment screen opens, so the
// badge is not whatever it was when the app loaded. Skipped if the list
// arrived in the last 15 seconds.
export const useFreshReaderStatus = () => {
  const { refreshReaderStatus } = useStripeTerminalContext();
  useEffect(() => {
    refreshReaderStatus({ maxAgeMs: 15_000 });
  }, [refreshReaderStatus]);
};

interface ReaderSummaryProps {
  // Opens the picker. Only offered when the shop has more than one reader.
  onChange?: () => void;
  // Hide Change reader while a payment is running.
  disabled?: boolean;
}

// One line on every payment screen saying which reader Process will use.
export const ReaderSummary = ({ onChange, disabled }: ReaderSummaryProps) => {
  const { resolvedReader, readers, hasMultipleReaders } =
    useStripeTerminalContext();
  useFreshReaderStatus();

  if (!resolvedReader) return null;

  const live = readers.find((r) => r.serial_number === resolvedReader.serial);

  return (
    <Stack gap={2}>
      <Group gap="xs" wrap="nowrap" justify="space-between">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <Text size="sm" fw={500} truncate>
            {live?.label || resolvedReader.label}
          </Text>
          <ReaderStatusBadge status={live?.status} />
        </Group>
        {hasMultipleReaders && onChange && !disabled && (
          <Anchor
            component="button"
            type="button"
            size="sm"
            onClick={onChange}
            style={{ flexShrink: 0 }}
          >
            Change reader
          </Anchor>
        )}
      </Group>
      {live && live.status !== "online" && (
        <Text size="xs" c="dimmed">
          The reader looks offline. Check its power and WiFi. You can still try
          the payment.
        </Text>
      )}
    </Stack>
  );
};

export default ReaderSummary;
